/**
 * CollaborativeContext - Real-time collaboration context using Y.js
 *
 * Provides:
 * - Y.js document for CRDT-based collaborative editing
 * - WebSocket provider for syncing with collaboration service
 * - Awareness state for user presence
 * - Connection status
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import type { Awareness } from 'y-protocols/awareness';

export interface CollaborativeUser {
  id: string;
  name: string;
  color: string;
  isAgent?: boolean;
  cursor?: {
    index: number;
    length: number;
  };
}

interface CollaborativeContextValue {
  ydoc: Y.Doc | null;
  provider: WebsocketProvider | null;
  awareness: Awareness | null;
  isConnected: boolean;
  isConnecting: boolean;
  /** True after the initial Yjs sync step 2 completes — doc has server's state */
  isSynced: boolean;
  connectionError: string | null;
  connectedUsers: CollaborativeUser[];
  connect: (docId: string, userId: string, userName: string) => void;
  disconnect: () => void;
}

const CollaborativeContext = createContext<CollaborativeContextValue | null>(null);

// Use relative WebSocket URL to go through Vite proxy in development
// In production, this would be configured via environment variable
function getCollabServiceUrl(): string {
  if (import.meta.env.VITE_COLLAB_SERVICE_URL) {
    return import.meta.env.VITE_COLLAB_SERVICE_URL;
  }
  // Use relative URL to work with Vite proxy
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/collab`;
}

interface CollaborativeProviderProps {
  children: ReactNode;
}

export function CollaborativeProvider({ children }: CollaborativeProviderProps) {
  const [ydoc, setYdoc] = useState<Y.Doc | null>(null);
  const [provider, setProvider] = useState<WebsocketProvider | null>(null);
  const [awareness, setAwareness] = useState<Awareness | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSynced, setIsSynced] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [connectedUsers, setConnectedUsers] = useState<CollaborativeUser[]>([]);

  // Use refs to avoid dependency cycles in callbacks
  const providerRef = useRef<WebsocketProvider | null>(null);
  const ydocRef = useRef<Y.Doc | null>(null);
  const awarenessCleanupRef = useRef<(() => void) | null>(null);

  // Stable disconnect function that uses refs
  const disconnect = useCallback(() => {
    // Clean up awareness listener
    if (awarenessCleanupRef.current) {
      awarenessCleanupRef.current();
      awarenessCleanupRef.current = null;
    }
    if (providerRef.current) {
      providerRef.current.disconnect();
      providerRef.current.destroy();
      providerRef.current = null;
    }
    if (ydocRef.current) {
      ydocRef.current.destroy();
      ydocRef.current = null;
    }
    setProvider(null);
    setYdoc(null);
    setAwareness(null);
    setIsConnected(false);
    setIsConnecting(false);
    setIsSynced(false);
    setConnectionError(null);
    setConnectedUsers([]);
  }, []); // No dependencies - uses refs

  // Stable connect function
  const connect = useCallback((docId: string, userId: string, userName: string) => {
    // Clean up existing connection
    disconnect();

    setIsConnecting(true);
    setConnectionError(null);

    const doc = new Y.Doc();
    const baseUrl = getCollabServiceUrl();
    // WebsocketProvider appends the roomName (docId) to the URL automatically,
    // so only pass the base URL here to avoid path duplication.
    // Include e2e_bypass in WS params for test environments.
    // Browsers cannot send custom headers on WebSocket upgrades, so nginx auth
    // expects it as a query param when X-E2E-Bypass header is unavailable.
    const wsParams: Record<string, string> = { userId, userName };
    const e2eSecret = import.meta.env.VITE_E2E_BYPASS_SECRET;
    if (e2eSecret) wsParams.e2e_bypass = e2eSecret;
    const wsProvider = new WebsocketProvider(baseUrl, docId, doc, {
      connect: true,
      params: wsParams,
    });

    // Store in refs for cleanup
    providerRef.current = wsProvider;
    ydocRef.current = doc;

    // Track connection status
    wsProvider.on('status', ({ status }: { status: string }) => {
      setIsConnecting(status === 'connecting');
      setIsConnected(status === 'connected');

      if (status === 'disconnected') {
        setIsSynced(false);
        setConnectionError('Disconnected from collaboration server');
      }
    });

    // Handle close code 4000 — server invalidated this room because the document
    // was updated via API. y-websocket will try to auto-reconnect with the stale
    // local Y.Doc, which would re-push old content. Instead: stop auto-reconnect,
    // destroy this connection, and reconnect fresh (empty Y.Doc). The MarthaEditor
    // 3-second fallback will then load the updated content from the document API.
    wsProvider.on('connection-close', (event: CloseEvent | null) => {
      if (event?.code === 4000) {
        console.log('[CollaborativeContext] Server invalidated room (code 4000) — reconnecting with fresh Y.Doc');
        // Prevent y-websocket from reconnecting with stale local state
        wsProvider.shouldConnect = false;
        // Fresh connect — creates a new empty Y.Doc so the fallback loads updated content
        setTimeout(() => connect(docId, userId, userName), 400);
      }
    });

    // isSynced fires after the initial Yjs sync step 2 completes.
    // This is AFTER status='connected' — the doc has the server's full state.
    wsProvider.on('synced', ({ synced }: { synced: boolean }) => {
      setIsSynced(synced);
    });

    // Track connected users via awareness
    const awarenessInstance = wsProvider.awareness;

    const updateUsers = () => {
      const seen = new Map<string, CollaborativeUser>();
      awarenessInstance.getStates().forEach((state) => {
        if (state.user) {
          const u = state.user as CollaborativeUser;
          // Dedup by user ID — keep latest entry
          seen.set(u.id, u);
        }
      });
      setConnectedUsers(Array.from(seen.values()));
    };

    awarenessInstance.on('change', updateUsers);
    updateUsers();

    // Store cleanup function so disconnect() can remove the listener
    awarenessCleanupRef.current = () => {
      awarenessInstance.off('change', updateUsers);
    };

    // Set local user info
    awarenessInstance.setLocalStateField('user', {
      id: userId,
      name: userName,
      color: getRandomColor(),
    });

    // Clean up awareness on page unload / tab close
    const handleBeforeUnload = () => {
      awarenessInstance.setLocalState(null);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        awarenessInstance.setLocalStateField('user', {
          id: userId,
          name: userName,
          color: awarenessInstance.getLocalState()?.user?.color || getRandomColor(),
          away: true,
        });
      } else {
        awarenessInstance.setLocalStateField('user', {
          id: userId,
          name: userName,
          color: awarenessInstance.getLocalState()?.user?.color || getRandomColor(),
        });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Extend cleanup to also remove these listeners
    const prevCleanup = awarenessCleanupRef.current;
    awarenessCleanupRef.current = () => {
      prevCleanup();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

    setYdoc(doc);
    setProvider(wsProvider);
    setAwareness(awarenessInstance);
  }, [disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Memoize — every consumer of this context lives in the editor critical
  // path. A fresh value object on every render cascades re-renders through
  // DocumentationReader → MarthaEditor → ProseMirror, and combined with an
  // unstable dep in a downstream useEffect will trip React #185.
  const value = useMemo<CollaborativeContextValue>(
    () => ({
      ydoc,
      provider,
      awareness,
      isConnected,
      isConnecting,
      isSynced,
      connectionError,
      connectedUsers,
      connect,
      disconnect,
    }),
    [
      ydoc,
      provider,
      awareness,
      isConnected,
      isConnecting,
      isSynced,
      connectionError,
      connectedUsers,
      connect,
      disconnect,
    ],
  );

  return (
    <CollaborativeContext.Provider value={value}>
      {children}
    </CollaborativeContext.Provider>
  );
}

export function useCollaboration() {
  const context = useContext(CollaborativeContext);
  if (!context) {
    throw new Error('useCollaboration must be used within a CollaborativeProvider');
  }
  return context;
}

// Utility to generate random presence colors
const PRESENCE_COLORS = [
  '#f97066', '#0ea5e9', '#22c55e', '#f59e0b', '#a855f7',
  '#ec4899', '#14b8a6', '#6366f1', '#84cc16', '#f43f5e',
];

function getRandomColor(): string {
  return PRESENCE_COLORS[Math.floor(Math.random() * PRESENCE_COLORS.length)];
}

export default CollaborativeContext;
